import { useEffect } from "react";
import { useStore } from "@/stores/useStore";
import type {
  SupportedLanguage,
  Prefecture,
  City,
  Location,
} from "../../types/geo.types";

export interface UseGeoLocationsOptions {
  prefectureCode?: number;
  citySlug?: string;
  cityId?: string;
  lang: SupportedLanguage;
}

export interface UseGeoLocationsResult {
  prefectures: Prefecture[];
  cities: City[];
  locations: Location[];
  loading: boolean;
  error: string | null;
  reloadPrefectures(): Promise<void>;
}

/**
 * Hook for accessing geo location data (prefectures, cities, locations).
 *
 * IMPORTANT: This hook should be used in components wrapped with `observer()`.
 * The MobX Map values are accessed directly (no useMemo) so that the observer
 * can track changes to the Map contents and trigger re-renders automatically.
 */
export function useGeoLocations(
  options: UseGeoLocationsOptions,
): UseGeoLocationsResult {
  const { geoStore } = useStore();
  const { prefectureCode, cityId, citySlug, lang } = options;

  // Trigger data fetching via side effects
  useEffect(() => {
    geoStore.loadPrefectures(lang);
  }, [geoStore, lang]);

  useEffect(() => {
    if (prefectureCode) {
      geoStore.loadCities(prefectureCode, lang);
    }
  }, [geoStore, prefectureCode, lang]);

  useEffect(() => {
    if (cityId || citySlug) {
      geoStore.loadLocations({ cityId, citySlug, lang });
    }
  }, [geoStore, cityId, citySlug, lang]);

  // Access MobX Map values directly (observer() handles reactivity)
  // Do NOT wrap in useMemo - it breaks MobX reactivity because Map references don't change
  const prefecturesData = geoStore.prefecturesByLocale.get(lang);
  const prefectures: Prefecture[] = Array.isArray(prefecturesData)
    ? prefecturesData
    : [];

  const citiesData = prefectureCode
    ? geoStore.citiesByPrefecture.get(`${lang}:${prefectureCode}`)
    : undefined;
  const cities: City[] = Array.isArray(citiesData) ? citiesData : [];

  const locationKey = citySlug ?? cityId;
  const locationsData = locationKey
    ? geoStore.locationsByCity.get(`${lang}:${locationKey}`)
    : undefined;
  const locations: Location[] = Array.isArray(locationsData)
    ? locationsData
    : [];

  return {
    prefectures,
    cities,
    locations,
    loading: geoStore.loading,
    error: geoStore.error,
    reloadPrefectures: async () => {
      // Force reload by clearing cache first
      geoStore.prefecturesByLocale.delete(lang);
      await geoStore.loadPrefectures(lang);
    },
  };
}
